/**
 * 性格模板管理页面
 * 用于管理 AI 角色的性格模板配置
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  message,
  Tag,
  Switch,
  Row,
  Col,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SmileOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  getPersonalityPage,
  addPersonality,
  updatePersonality,
  deletePersonality,
  updatePersonalityStatus,
  type PersonalityTemplate
} from '../services/api/personality';
import { useModal } from '../hooks';
import SearchBar from '../components/common/SearchBar';
import EmptyWarm from '../components/common/EmptyWarm';
import LoadingWarm from '../components/common/LoadingWarm';
import './PersonalityTemplate.css';

const { TextArea } = Input;
const { Text } = Typography;

const PersonalityTemplatePage: React.FC = () => {
  const modal = useModal();
  const [form] = Form.useForm();
  const [personalities, setPersonalities] = useState<PersonalityTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: ''
  });
  const [editingPersonality, setEditingPersonality] = useState<PersonalityTemplate | null>(null);

  // 获取性格模板列表
  const fetchPersonalities = async () => {
    setLoading(true);
    try {
      const res = await getPersonalityPage({
        current,
        size: pageSize,
        keyword: searchParams.keyword || undefined
      });
      setPersonalities(res.records || []);
      setTotal(res.total || 0);
    } catch (error) {
      console.error('获取性格模板列表失败:', error);
      message.error('获取性格模板列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonalities();
  }, [current, pageSize, searchParams]);

  // 删除性格模板
  const handleDelete = async (personality: PersonalityTemplate) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除性格模板 "${personality.name}" 吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deletePersonality(personality.id);
          message.success('性格模板删除成功');
          fetchPersonalities();
        } catch (error) {
          console.error('删除性格模板失败:', error);
          message.error('删除性格模板失败');
        }
      }
    });
  };

  // 保存性格模板
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        id: editingPersonality?.id,
        ...values
      };

      if (editingPersonality) {
        await updatePersonality(data);
        message.success('性格模板更新成功');
      } else {
        await addPersonality(data);
        message.success('性格模板创建成功');
      }

      modal.hideModal();
      form.resetFields();
      setEditingPersonality(null);
      fetchPersonalities();
    } catch (error) {
      console.error('操作失败:', error);
    }
  };

  // 切换启用状态
  const handleToggleStatus = async (personality: PersonalityTemplate) => {
    try {
      await updatePersonalityStatus(personality.id, !personality.isEnabled);
      message.success(personality.isEnabled ? '已禁用该性格模板' : '已启用该性格模板');
      fetchPersonalities();
    } catch (error) {
      console.error('更新状态失败:', error);
      message.error('更新状态失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '🎭 模板名称',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '🎨 图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 80,
      render: (icon?: string) => icon || '😊'
    },
    {
      title: '📝 描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200
    },
    {
      title: '🎙️ 语音风格',
      dataIndex: 'voiceStyle',
      key: 'voiceStyle',
      width: 120,
      render: (voiceStyle?: string) => voiceStyle || '-'
    },
    {
      title: '📊 状态',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      render: (isEnabled: boolean) => (
        <Tag color={isEnabled ? 'success' : 'default'}>
          {isEnabled ? '✅ 启用' : '⏸️ 禁用'}
        </Tag>
      )
    },
    {
      title: '🔢 排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      sorter: (a: PersonalityTemplate, b: PersonalityTemplate) => a.sortOrder - b.sortOrder
    },
    {
      title: '⚙️ 操作',
      key: 'action',
      width: 220,
      render: (_: any, record: PersonalityTemplate) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingPersonality(record);
              form.setFieldsValue(record);
              modal.showModal();
            }}
          >
            编辑
          </Button>
          <Switch
            size="small"
            checked={record.isEnabled}
            onChange={() => handleToggleStatus(record)}
            checkedChildren="启"
            unCheckedChildren="禁"
          />
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="personality-template-container">
      <Card
        className="warm-card"
        title={
          <Space>
            <SmileOutlined className="personality-template-title-icon" />
            <span>性格模板管理</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingPersonality(null);
              form.resetFields();
              modal.showModal();
            }}
          >
            🌸 新增模板
          </Button>
        }
        bordered={false}
      >
        <div className="personality-template-search-bar">
          <SearchBar
            placeholder="搜索模板名称或描述..."
            onSearch={(value) => {
              setSearchParams({ keyword: value });
              setCurrent(1);
            }}
            width={320}
          />
        </div>

        {loading ? (
          <LoadingWarm type="tree" tip="性格模板数据正在路上~" />
        ) : (
          <Table
            dataSource={personalities}
            columns={columns}
            rowKey="id"
            pagination={{
              current,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, size) => {
                setCurrent(page);
                setPageSize(size || 10);
              }
            }}
            locale={{ emptyText: <EmptyWarm type="search" /> }}
          />
        )}
      </Card>

      {/* 新增/编辑性格模板模态框 */}
      <Modal
        title={editingPersonality ? '✏️ 编辑性格模板' : '➕ 新增性格模板'}
        open={modal.visible}
        onOk={handleSave}
        onCancel={modal.handleCancel}
        confirmLoading={modal.confirmLoading}
        okText="保存"
        cancelText="取消"
        width={700}
        className="warm-modal"
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="🎭 模板名称"
                rules={[{ required: true, message: '请输入模板名称!' }]}
              >
                <Input placeholder="例如：温柔治愈、活泼可爱、知性优雅" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="icon"
                label="🎨 图标"
              >
                <Input placeholder="例如：😊🌸✨" maxLength={10} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="voiceStyle"
                label="🎙️ 语音风格"
              >
                <Input placeholder="例如：温柔、活泼、沉稳" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="sortOrder"
                label="🔢 排序"
                initialValue={0}
              >
                <Input type="number" placeholder="数字越小越靠前" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="📝 描述"
          >
            <TextArea
              rows={2}
              placeholder="请输入模板描述"
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="promptTemplate"
            label="📋 提示词模板"
            rules={[{ required: true, message: '请输入提示词模板!' }]}
          >
            <TextArea
              rows={6}
              placeholder="请输入 AI 角色的提示词模板，用于定义角色的性格和行为方式"
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="defaultExpressions"
            label="💬 默认表情"
          >
            <TextArea
              rows={3}
              placeholder="请输入默认表情包，用逗号分隔"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="isEnabled"
            label="📊 状态"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren="✅ 启用"
              unCheckedChildren="⏸️ 禁用"
              defaultChecked
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PersonalityTemplatePage;
